import { SelectionChangedEvent } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useIsFetching } from 'react-query';
import { ComposedTable } from 'components';
import { SkeletonPageLayout } from 'components/SkeletonLoader/SkeletonPageLayout';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { DEFAULT_COL_WIDTH } from '../VendorsTable/constants';
import { labels } from './messages';
import { PrintChecksFooter } from './PrintChecksFooter';
import {
  setCancelBtnDisabled,
  setIsLoadingPrintChecks,
  setSelectedVendors,
  setPrintChecksData,
  getIsLoadingPrintChecks
} from './slice';
import { PrintChecksVendorData } from './types';
import { usePrintChecksData } from './usePrintChecksData';
import { usePrintChecksTableColDefs } from './usePrintChecksTableColDefs';

export const PrintChecksTable = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const isLoadingPrintChecks = useAppSelector(getIsLoadingPrintChecks);
  const isFetchingPrintChecks = useIsFetching('printAuthorizedVendors');
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const intl = useIntl();

  const { data, isLoading, isFetching, rowData } = usePrintChecksData(
    result => {
      if (result.vendors.length) {
        dispatch(setPrintChecksData(result.vendors));
      }
    },
    true
  );

  const isTableLoading = isFetching;

  useEffect(() => {
    dispatch(setIsLoadingPrintChecks(isLoading));
  }, [dispatch, isLoading]);

  useEffect(() => {
    dispatch(setCancelBtnDisabled(isLoading));
  }, [dispatch, isLoading]);

  const columnDefs = usePrintChecksTableColDefs();

  const onSearchInputChange = (newValue: string): void =>
    setSearchInputValue(newValue);

  const handleRowSelection = ({ api }: SelectionChangedEvent): void => {
    dispatch(setSelectedVendors(api.getSelectedRows()));
  };

  if (isFetching) {
    return <SkeletonPageLayout />;
  }

  return (
    <>
      <ComposedTable<PrintChecksVendorData>
        columnDefs={columnDefs}
        defaultColDef={{
          minWidth: DEFAULT_COL_WIDTH
        }}
        getRowId={param => param.data.vendorNo}
        isDownloadable={false}
        isSearchable={!isTableLoading}
        isLoading={isTableLoading}
        isPrintable={false}
        onGridSizeChanged={({ api }) => api.sizeColumnsToFit()}
        onSearchInputChange={onSearchInputChange}
        searchInputValue={searchInputValue}
        onSelectionChanged={handleRowSelection}
        rowData={rowData}
        suppressRowClickSelection
        suppressRowDeselection
        tableHeaderCount={data?.totalNoOfVendors}
        tableHeaderTitle={intl.formatMessage(labels.printChecksTable)}
      />
      {!isLoadingPrintChecks && !isFetchingPrintChecks && (
        <PrintChecksFooter data={data ?? undefined} />
      )}
    </>
  );
};
